'use client';

import type { Cart, CartLine, Money, Product, ProductVariant } from '@/lib/shopify/types';

/**
 * Meta Pixel (browser side). Advertising, so it has its OWN consent ("marketing"), separate from the
 * Google Analytics one. Until the visitor grants it, nothing is loaded and nothing is sent: no script,
 * no `fbq` call, no cookie. There is no <noscript> pixel on purpose (it would send a PageView without
 * consent).
 *
 * Inert unless NEXT_PUBLIC_META_PIXEL_ID is set (it is inlined at build time).
 *
 * Deliberately not used: automatic events/configuration (button clicks, page metadata), the pixel's own
 * history tracking (PageView is sent by us, once per page, from the Next.js router) and any customer
 * data such as email or phone. Values always come from Shopify (variant price, cart line totals).
 * Purchase is not sent from here: checkout runs on Shopify's domain, so it goes server to server
 * (see lib/meta-server.ts).
 */

export type MarketingConsent = 'granted' | 'denied' | null;

export const MARKETING_CONSENT_KEY = 'nomafe_marketing_consent_v1';
export const MARKETING_CONSENT_EVENT = 'nomafe:marketing-consent';

/**
 * Version of the consent banner. '2' = the banner that asks for Analytics AND Marketing separately.
 * It is stored together with the marketing decision, and a marketing decision only counts while the
 * stored version is the current one. So an older analytics-only consent is never read as marketing
 * consent, visitors who only have that old consent are asked once, and bumping this constant asks
 * everybody again (e.g. if the policy changes).
 */
export const CONSENT_VERSION = '2';
export const CONSENT_VERSION_KEY = 'nomafe_consent_version';

const rawId = process.env.NEXT_PUBLIC_META_PIXEL_ID?.trim() ?? '';
const pixelId = /^\d{8,20}$/.test(rawId) ? rawId : '';

/** False when no valid pixel id is configured: the whole integration (and its banner option) is off. */
export const metaPixelEnabled = Boolean(pixelId);

type Fbq = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  loaded: boolean;
  version: string;
  push: Fbq;
  disablePushState?: boolean;
  allowDuplicatePageViews?: boolean;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let initialized = false;
let revoked = false;

const versionIsCurrent = () => {
  try {
    return window.localStorage.getItem(CONSENT_VERSION_KEY) === CONSENT_VERSION;
  } catch {
    return false;
  }
};

export function readMarketingConsent(): MarketingConsent {
  if (typeof window === 'undefined' || !versionIsCurrent()) return null;
  try {
    const value = window.localStorage.getItem(MARKETING_CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

/** True for a visitor who answered an older banner: they keep their analytics choice and are asked about marketing once. */
export function needsConsentRefresh(analyticsDecision: 'granted' | 'denied' | null) {
  return metaPixelEnabled && typeof window !== 'undefined' && analyticsDecision !== null && !versionIsCurrent();
}

const allowed = () => metaPixelEnabled && typeof window !== 'undefined' && readMarketingConsent() === 'granted';

/** Same stub as Meta's base code, so calls made before fbevents.js arrives are queued. */
function createStub() {
  const stub = function fbq() {
    // eslint-disable-next-line prefer-rest-params
    const args = arguments;
    if (stub.callMethod) stub.callMethod.apply(stub, args as unknown as unknown[]);
    else stub.queue.push(args);
  } as unknown as Fbq;
  stub.push = stub;
  stub.loaded = true;
  stub.version = '2.0';
  stub.queue = [];
  // PageView is sent by us on every Next.js navigation; the pixel must not also react to pushState
  stub.disablePushState = true;
  // Meta's pixel drops a second PageView in the same page load unless told otherwise. This site is a Next.js SPA:
  // each client-side navigation sends its own PageView (once per page, guarded in AnalyticsProvider).
  stub.allowDuplicatePageViews = true;
  window.fbq = stub;
  window._fbq = stub;
}

/** Loads fbevents.js and registers the pixel, once, and only with marketing consent. No PageView here. */
export function initializeMetaPixel() {
  if (initialized || !allowed()) return;
  initialized = true;
  if (!window.fbq) createStub();
  window.fbq?.('set', 'autoConfig', false, pixelId); // no automatic events / metadata scraping
  window.fbq?.('init', pixelId);

  if (!document.querySelector(`script[data-nomafe-meta="${pixelId}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.dataset.nomafeMeta = pixelId;
    document.head.appendChild(script);
  }
}

function expireMetaCookies() {
  const labels = window.location.hostname.split('.');
  const domains = labels.slice(0, -1).map((_, i) => `.${labels.slice(i).join('.')}`); // .www.x.com, .x.com
  for (const name of ['_fbp', '_fbc']) {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    for (const domain of domains) document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`;
  }
}

/** Stores the decision and applies it. A withdrawal stops every later call and removes Meta's cookies. */
export function updateMarketingConsent(consent: Exclude<MarketingConsent, null>) {
  try {
    window.localStorage.setItem(MARKETING_CONSENT_KEY, consent);
    window.localStorage.setItem(CONSENT_VERSION_KEY, CONSENT_VERSION);
  } catch {
    /* storage unavailable: the decision still applies to this page */
  }

  if (consent === 'granted') {
    initializeMetaPixel();
    if (revoked) {
      window.fbq?.('consent', 'grant');
      revoked = false;
    }
  } else {
    if (window.fbq) {
      window.fbq('consent', 'revoke');
      revoked = true;
    }
    expireMetaCookies();
  }

  window.dispatchEvent(new CustomEvent(MARKETING_CONSENT_EVENT, { detail: consent }));
}

function track(name: string, data?: Record<string, unknown>) {
  if (!allowed()) return;
  initializeMetaPixel();
  if (!window.fbq) return;
  if (data) window.fbq('track', name, data);
  else window.fbq('track', name);
}

// ---------- Values (always Shopify's) ----------

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const toNumber = (value: string) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Meta content id = numeric Shopify variant id ('gid://shopify/ProductVariant/123' → '123'). */
export const metaContentId = (variantId: string) => /(\d+)$/.exec(variantId)?.[1] ?? variantId;

/** Unit price after discounts as charged by Shopify for that cart line. */
function lineUnit(line: CartLine): { unit: number; currency: string } | null {
  const total = toNumber(line.cost.totalAmount.amount);
  if (total === null || line.quantity < 1) return null;
  return { unit: round2(total / line.quantity), currency: line.cost.totalAmount.currencyCode };
}

// ---------- Standard events ----------

export function metaPageView() {
  track('PageView');
}

/** `price` is the unit price shown on the page (Shopify's, or the announced sale price Shopify confirms). */
export function metaViewContent(product: Product, variant: ProductVariant, price: Money) {
  const unit = toNumber(price.amount);
  const id = metaContentId(variant.id);
  track('ViewContent', {
    content_type: 'product',
    content_ids: [id],
    content_name: product.title,
    ...(unit === null
      ? {}
      : { contents: [{ id, quantity: 1, item_price: unit }], value: round2(unit), currency: price.currencyCode }),
  });
}

/**
 * `cart` is the cart Shopify returned after the add, so the value is what Shopify really charges for
 * those units (promo included). If the line is not there, no value is sent rather than a guessed one.
 */
export function metaAddToCart(product: Product, variant: ProductVariant, quantity: number, cart?: Cart | null) {
  if (quantity < 1) return;
  const id = metaContentId(variant.id);
  const line = cart?.lines.find((l) => l.merchandise.id === variant.id);
  const priced = line ? lineUnit(line) : null;
  track('AddToCart', {
    content_type: 'product',
    content_ids: [id],
    content_name: product.title,
    ...(priced
      ? {
          contents: [{ id, quantity, item_price: priced.unit }],
          value: round2(priced.unit * quantity),
          currency: priced.currency,
        }
      : { contents: [{ id, quantity }] }),
  });
}

/** Value = sum of the cart lines after discounts, without taxes or shipping that Shopify adds at checkout. */
export function metaInitiateCheckout(cart: Cart) {
  if (!cart.lines.length) return;
  const contents: { id: string; quantity: number; item_price?: number }[] = [];
  let value = 0;
  let complete = true;
  let currency = '';
  for (const line of cart.lines) {
    const priced = lineUnit(line);
    const id = metaContentId(line.merchandise.id);
    if (!priced) {
      complete = false;
      contents.push({ id, quantity: line.quantity });
      continue;
    }
    currency = currency || priced.currency;
    value += priced.unit * line.quantity;
    contents.push({ id, quantity: line.quantity, item_price: priced.unit });
  }
  const titles = new Set(cart.lines.map((l) => l.merchandise.product.title));
  track('InitiateCheckout', {
    content_type: 'product',
    content_ids: contents.map((c) => c.id),
    contents,
    num_items: cart.lines.reduce((sum, l) => sum + l.quantity, 0),
    ...(titles.size === 1 ? { content_name: [...titles][0] } : {}),
    ...(complete && currency ? { value: round2(value), currency } : {}),
  });
}

// ---------- Browser ids for the server-side Purchase ----------

const FB_ID = /^fb\.[0-2]\.\d{10,13}\.[A-Za-z0-9_.-]{4,90}$/;

function readCookie(name: string) {
  const entry = document.cookie.split(';').find((c) => c.trim().startsWith(`${name}=`));
  return entry ? decodeURIComponent(entry.trim().slice(name.length + 1)) : null;
}

/** Meta's own first-party cookies (only exist after the pixel ran with consent). Never invented. */
export function readMetaBrowserIds(): { fbp?: string; fbc?: string } {
  if (!allowed()) return {};
  const ids: { fbp?: string; fbc?: string } = {};
  const fbp = readCookie('_fbp');
  const fbc = readCookie('_fbc');
  if (fbp && FB_ID.test(fbp)) ids.fbp = fbp;
  if (fbc && FB_ID.test(fbc)) ids.fbc = fbc;
  return ids;
}
